import { useEffect, useState } from 'react';
import ConfirmModal from './ConfirmModal.tsx';
import { onStagingPrompt, resolveStagingPrompt, stagingEnv, type StagingPrompt } from '../lib/stagingGuard.ts';

/**
 * The two things staging shows that production does not: a strip across the top
 * saying where you are, and a dialog in front of anything that would spend AI
 * credits.
 *
 * Both live here and both mount once, in App.tsx above the router, because
 * there is no shared app chrome to hang them on — every route renders its own
 * bar. Off staging the whole file renders null.
 */

/** Sets `data-staging` on <html> so the CSS can reserve the strip's height on
 *  every surface without each one importing anything. Returns null until the
 *  answer lands, which is one request and no flash: the strip appearing a beat
 *  late is better than it appearing and then vanishing on production. */
function useStaging(): { staging: boolean; liveUrl: string } | null {
  const [env, setEnv] = useState<{ staging: boolean; liveUrl: string } | null>(null);
  useEffect(() => {
    let live = true;
    void stagingEnv().then((e) => {
      if (live) setEnv(e);
    });
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (!env) return;
    const root = document.documentElement;
    if (env.staging) root.setAttribute('data-staging', '');
    else root.removeAttribute('data-staging');
  }, [env]);
  return env;
}

export function StagingBanner() {
  const env = useStaging();
  if (!env?.staging) return null;
  return (
    <div className="staging-banner" role="status">
      <span className="staging-banner__tag">Staging</span>
      {/* The sentence is what gets dropped on a phone, never the link: a
          truncated "Go to t…" is the one thing in this strip somebody might
          actually tap. */}
      <span className="staging-banner__says">A copy of Studio for testing. Nothing made here is real.</span>
      <a className="staging-banner__link" href={env.liveUrl}>
        Go to the live studio
      </a>
    </div>
  );
}

export function StagingGuardHost() {
  const [prompt, setPrompt] = useState<StagingPrompt | null>(null);
  useEffect(() => onStagingPrompt(setPrompt), []);
  if (!prompt) return null;
  return (
    <ConfirmModal
      title="You are on the staging site"
      body={
        <>
          <p>
            This is a copy of Studio for testing. Nothing made here is the real thing, and {prompt.what} costs exactly
            what it would on the live site.
          </p>
          <p>
            Real work belongs at <strong>{prompt.liveUrl.replace(/^https?:\/\//, '')}</strong>.
          </p>
        </>
      }
      confirmLabel="Take me to the live studio"
      // The primary is the way out, and continuing is the quiet middle button —
      // see ConfirmModal's own note on why a secondary is never styled as the
      // primary.
      //
      // Enter is OFF here. This dialog opens on a keystroke (the Enter that
      // sent a chat message), the keystroke can carry into it, and the primary
      // LEAVES THE PAGE — observed once in testing, which took the author to a
      // different studio with their message unsent. Escape still cancels.
      enterConfirms={false}
      onConfirm={() => {
        resolveStagingPrompt(false);
        window.location.assign(prompt.liveUrl);
      }}
      // SHORT AND CONSTANT. This carried the action ("Continue and import this
      // case study") until 2026-08-20, which put somebody's copy inside a
      // fixed-width dialog's footer and burst it. The action is named in the
      // body above, where a sentence has room to be a sentence.
      secondaryLabel="Continue anyway"
      onSecondary={() => resolveStagingPrompt(true)}
      onCancel={() => resolveStagingPrompt(false)}
    />
  );
}
